import type { CatalogAssetKind, CatalogCategory } from '~convex/lib/catalogEnums'
import { CATALOG_ASSET_KINDS } from '~convex/lib/catalogEnums'
import { isCatalogCategory } from './catalog-labels'
import type { DimensionBand } from './catalog-query'

// The Catálogo page's URL state.
//
// Every filter lives in the URL, which is what makes a filtered view a
// shareable link and lets the grouped landing be nothing but "no filters" —
// there is no mode flag, and there cannot be one that disagrees with what the
// chips say.

/** How dense the results grid is. Remembered per browser, not in the URL. */
export type CatalogView = 'grid' | 'dense' | 'list'

export const CATALOG_VIEW_STORAGE_KEY = 'planno.catalog.view'

/**
 * The two halves of the "Produtos salvos" chip, as URL values.
 *
 * ENGLISH, like every other technical contract in this codebase — the route
 * names, the server enums these map onto, the field names either side. A URL
 * is machine-facing state that happens to be legible; the chip's own copy is
 * where the reader gets pt-BR, and it is translated.
 */
export const PRODUCT_ORIGIN_PARAMS = ['catalog', 'link', 'manual'] as const
export type ProductOriginParam = (typeof PRODUCT_ORIGIN_PARAMS)[number]

export const PRODUCT_USE_PARAMS = ['project', 'moodboard'] as const
export type ProductUseParam = (typeof PRODUCT_USE_PARAMS)[number]

export type CatalogSearch = {
  brand?: string
  cat?: CatalogCategory
  col?: string
  color?: string[]
  /** Diameter band — a round table or a pendant is measured by Ø, not width. */
  diamax?: number
  diamin?: number
  dmax?: number
  dmin?: number
  files?: CatalogAssetKind[]
  hmax?: number
  hmin?: number
  mat?: string[]
  /**
   * "Produtos salvos" with nothing narrowed. A separate key from `origem` and
   * `uso` because the chip being ON and the chip having a selection are
   * different states — without it, clearing every option inside the popover
   * would silently put the whole catalog back.
   */
  mine?: boolean
  /**
   * "Novidades" — products first seen within this many days.
   *
   * A NUMBER rather than a flag, and the same key and meaning the admin
   * catalog's own `new` carries. TanStack merges every route's search into
   * one shape, so two routes cannot spell the same key with different types;
   * matching admin is better than inventing a second word for the same
   * question. The chip writes `NEW_WITHIN_DAYS`.
   */
  new?: number
  /** Produtos salvos › Origem. */
  origin?: ProductOriginParam[]
  pmax?: number
  pmin?: number
  q?: string
  sub?: string
  /**
   * "Ver todos" — flatten the grouped page with no filter applied. A real
   * param rather than a fake chip, because it IS a view choice and pretending
   * otherwise would mean a chip the user cannot understand or remove.
   */
  all?: boolean
  /** Produtos salvos › Em uso. */
  use?: ProductUseParam[]
  wmax?: number
  wmin?: number
}

/** The window "Novidades" means, and the only value its chip writes. */
export const NEW_WITHIN_DAYS = 30
const INTEGER_URL_VALUE = /^\d+$/

/**
 * A day count a URL may carry — bounded and normalized to a number before it
 * reaches Convex. Integer strings are valid URL encodings; partial parses such
 * as `30days`, decimals and signs are not.
 */
const asDayWindow = (value: unknown): number | undefined => {
  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && INTEGER_URL_VALUE.test(value)
        ? Number(value)
        : Number.NaN
  return Number.isInteger(parsed) && parsed > 0 && parsed <= 365 ? parsed : undefined
}

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() !== '' ? value : undefined

const asNumber = (value: unknown): number | undefined => {
  const parsed = typeof value === 'number' ? value : Number.parseFloat(String(value ?? ''))
  return Number.isFinite(parsed) ? parsed : undefined
}

const asStringList = (value: unknown): string[] | undefined => {
  const raw = Array.isArray(value) ? value : asString(value)?.split(',')
  const list = (raw ?? []).flatMap((entry) => (typeof entry === 'string' ? [entry.trim()] : []))
  return list.length === 0 ? undefined : list
}

const asOneOf = <T extends string>(value: unknown, allowed: readonly T[]): T[] | undefined => {
  const list = asStringList(value)
  const kept = (list ?? []).filter((entry): entry is T =>
    allowed.some((option) => option === entry)
  )
  return kept.length === 0 ? undefined : kept
}

const asAssetKinds = (value: unknown): CatalogAssetKind[] | undefined => {
  const list = asStringList(value)
  const kinds = (list ?? []).filter((entry): entry is CatalogAssetKind =>
    CATALOG_ASSET_KINDS.some((kind) => kind === entry)
  )
  return kinds.length === 0 ? undefined : kinds
}

/** Whitelisted decoder for the Catálogo search shape. */
export const decodeCatalogSearch = (search: Record<string, unknown>): CatalogSearch => {
  const category = isCatalogCategory(search.cat) ? search.cat : undefined
  return {
    all: search.all === true || search.all === 'true' ? true : undefined,
    brand: asString(search.brand),
    cat: category,
    col: asString(search.col),
    color: asStringList(search.color),
    diamax: asNumber(search.diamax),
    diamin: asNumber(search.diamin),
    dmax: asNumber(search.dmax),
    dmin: asNumber(search.dmin),
    files: asAssetKinds(search.files),
    hmax: asNumber(search.hmax),
    hmin: asNumber(search.hmin),
    mat: asStringList(search.mat),
    mine: search.mine === true || search.mine === 'true' ? true : undefined,
    new: asDayWindow(search.new),
    origin: asOneOf(search.origin, PRODUCT_ORIGIN_PARAMS),
    pmax: asNumber(search.pmax),
    pmin: asNumber(search.pmin),
    q: asString(search.q),
    // Subcategory keys are scoped by category and are not globally unique.
    sub: category === undefined ? undefined : asString(search.sub),
    use: asOneOf(search.use, PRODUCT_USE_PARAMS),
    wmax: asNumber(search.wmax),
    wmin: asNumber(search.wmin),
  }
}

/** Select a tree category without discarding orthogonal catalog facets. */
export const withCatalogCategory = (
  search: CatalogSearch,
  category: CatalogCategory
): CatalogSearch => ({ ...search, cat: category, sub: undefined })

/** Open the ungrouped catalog without discarding filters outside the tree. */
export const withoutCatalogCategory = (search: CatalogSearch): CatalogSearch => ({
  ...search,
  cat: undefined,
  sub: undefined,
})

/**
 * Every key the Catálogo page owns. Enumerated rather than derived, because
 * the router hands `useSearch` the whole INHERITED search object — the
 * workspace layout's `task`, `visit`, `newProject`, `onboarding` all ride
 * along — and walking `Object.values` there meant an unrelated param from a
 * parent route silently turned the grouped landing into a flat result grid.
 */
const CATALOG_SEARCH_KEYS = [
  'brand',
  'cat',
  'col',
  'color',
  'diamax',
  'diamin',
  'dmax',
  'dmin',
  'files',
  'hmax',
  'hmin',
  'all',
  'mat',
  'mine',
  'new',
  'origin',
  'pmax',
  'pmin',
  'q',
  'sub',
  'use',
  'wmax',
  'wmin',
] as const satisfies readonly (keyof CatalogSearch)[]

/**
 * Whether anything is filtering. THE grouped-landing test: no query, no chip
 * → the page is rails. There is no separate mode state, so the two can never
 * disagree.
 */
export const isCatalogFiltered = (search: CatalogSearch): boolean =>
  CATALOG_SEARCH_KEYS.some((key) => search[key] !== undefined)

/** How many chips are active — the number the "Limpar" row reports. */
export const catalogFilterCount = (search: CatalogSearch): number => {
  const { q, all, ...filters } = search
  const pairs: [unknown, unknown][] = [
    [filters.diamin, filters.diamax],
    [filters.wmin, filters.wmax],
    [filters.dmin, filters.dmax],
    [filters.hmin, filters.hmax],
    [filters.pmin, filters.pmax],
  ]
  const singles = [filters.brand, filters.cat, filters.col, filters.sub, filters.new]
  const lists = [filters.color, filters.files, filters.mat]
  return (
    pairs.filter(([min, max]) => min !== undefined || max !== undefined).length +
    singles.filter((value) => value !== undefined).length +
    lists.filter((value) => value !== undefined).length +
    // Produtos salvos counts ONCE however many options are ticked inside it —
    // it is one chip on the row, and the number under "Limpar" counts chips.
    (isMineOn(search) ? 1 : 0)
  )
}

/**
 * Is "Produtos salvos" on? Any of its three keys, because a URL that names an
 * Origem or an Em uso without `mine` is still the chip — a shared link should
 * not depend on which of them the sender happened to click first.
 */
export const isMineOn = (search: CatalogSearch): boolean =>
  search.mine === true || search.origin !== undefined || search.use !== undefined

/**
 * "Em uso" values are the URL's own spelling of the server's; `moodboard` is
 * `board` there, because the server names the TABLE and the URL names the
 * feature. The one pair that genuinely differs, so the one pair still mapped.
 */
const MINE_USE: Record<ProductUseParam, 'board' | 'project'> = {
  moodboard: 'board',
  project: 'project',
}

/**
 * The `mine` argument a URL state describes, or `undefined` when the chip is
 * off. `origin` passes straight through now that both sides spell it the
 * same — the table that used to translate `catalogo`/`mao` existed only to
 * bridge the two languages.
 */
export const mineArgs = (search: CatalogSearch) =>
  isMineOn(search)
    ? {
        origin: search.origin,
        usedIn: search.use?.map((value) => MINE_USE[value]),
      }
    : undefined

/** The band a pair of URL params describes, or `undefined` when neither is set. */
export const bandOf = (min?: number, max?: number): DimensionBand | undefined =>
  min === undefined && max === undefined
    ? undefined
    : { max: max ?? Number.MAX_SAFE_INTEGER, min: min ?? 0 }

/** The Convex `browse` / `search` arguments a URL state describes. */
export const catalogSearchArgs = (search: Omit<CatalogSearch, 'all'>) => ({
  assetKinds: search.files,
  brandSlug: search.brand,
  category: search.cat,
  collectionSlug: search.col,
  colors: search.color,
  depthCm: bandOf(search.dmin, search.dmax),
  diameterCm: bandOf(search.diamin, search.diamax),
  heightCm: bandOf(search.hmin, search.hmax),
  materials: search.mat,
  maxPriceCents: search.pmax === undefined ? undefined : Math.round(search.pmax * 100),
  minPriceCents: search.pmin === undefined ? undefined : Math.round(search.pmin * 100),
  newWithinDays: search.new,
  q: search.q,
  subcategoryKey: search.sub,
  widthCm: bandOf(search.wmin, search.wmax),
})
